import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { CalendarCheck2, CalendarClock, PhoneCall, Settings2 } from "lucide-react";
import { apiClient } from "../../App";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

/**
 * Planning-call automation (backend: services/planning_call_reminders.py).
 *
 * The welcome email at activation carries the Production Planning Call link.
 * Once a coordinator (Arie Berti) is picked here it is sent from her, and — when
 * the mode is armed — the client is reminded until the call is booked, with a
 * "call them" task for her after a few days. Booked = the toggle below, a
 * Fathom call recorded after the welcome email, or a booking on the GHL
 * planning-call calendar made by the client's exact email address.
 */

const MODE_COPY = {
  paused: { label: "Paused", tone: "bg-secondary text-muted-foreground", help: "No reminders are sent and no call tasks are created." },
  test: { label: "Test", tone: "bg-amber-100 text-amber-800", help: "Every reminder goes to the test inbox instead of the client. Call tasks are created marked [Test]." },
  live: { label: "Live", tone: "bg-green-100 text-green-800", help: "Clients get reminders and the coordinator gets call tasks." },
};

const BOOKED_SOURCE_COPY = {
  fathom: "Picked up from a Fathom call",
  ghl: "Booked on the GHL calendar",
  manual: "Marked by hand",
};

function fmtDate(value) {
  if (!value) return "";
  return new Date(value).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function todayIso() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export function planningCallState(p) {
  if (!p) return null;
  if (p.planning_call_booked_at) {
    return {
      key: "booked",
      label: `Booked ${fmtDate(p.planning_call_booked_at)}`,
      detail: BOOKED_SOURCE_COPY[p.planning_call_booked_source] || "Marked by hand",
      tone: "bg-green-100 text-green-800",
    };
  }
  if (!p.kickoff_email_sent) {
    return { key: "not_sent", label: "Welcome email not sent", detail: "No booking link has gone out", tone: "bg-secondary text-muted-foreground" };
  }
  const n = p.planning_call_reminders_sent || 0;
  const reminders = n ? `${n} reminder${n === 1 ? "" : "s"} sent` : "No reminders yet";
  if (p.planning_call_escalated_at) {
    return { key: "call", label: "Not booked — call the client", detail: reminders, tone: "bg-red-100 text-red-800" };
  }
  return { key: "waiting", label: "Not booked yet", detail: reminders, tone: "bg-amber-100 text-amber-800" };
}

/** Mark booked / un-mark. Calls onSaved with the fresh planning-call fields. */
export function PlanningCallBookedControl({ programId, status, onSaved, align = "end" }) {
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState(todayIso());
  const [saving, setSaving] = useState(false);
  const booked = Boolean(status?.planning_call_booked_at);

  async function save(isBooked) {
    setSaving(true);
    try {
      const res = await apiClient.put(`/programs/${programId}/planning-call`, {
        booked: isBooked,
        booked_at: isBooked ? date : null,
      });
      toast.success(isBooked ? "Planning call marked booked — reminders stop" : "Planning call marked not booked");
      setOpen(false);
      onSaved?.(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't update the planning call");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="h-7 rounded-full text-xs" data-testid="planning-call-booked-btn">
          <CalendarCheck2 className="w-3.5 h-3.5 mr-1.5" />
          {booked ? "Change" : "Mark booked"}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72" align={align}>
        {booked ? (
          <div className="space-y-3">
            <p className="text-sm">Booked {fmtDate(status.planning_call_booked_at)}.</p>
            <Button variant="outline" size="sm" className="w-full" disabled={saving} onClick={() => save(false)}>
              Not booked after all
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Planning call date</Label>
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
            </div>
            <p className="text-xs text-muted-foreground">Stops the reminder emails to the client.</p>
            <Button size="sm" className="w-full" disabled={saving || !date} onClick={() => save(true)}>
              {saving ? "Saving..." : "Planning call booked"}
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

/** Table cell for New Projects → Active. */
export function PlanningCallStatusCell({ program, onChange }) {
  const state = planningCallState(program);
  return (
    <div className="flex flex-col items-start gap-1.5">
      <Badge variant="secondary" className={`${state.tone} font-normal`}>{state.label}</Badge>
      <span className="text-[11px] text-muted-foreground">{state.detail}</span>
      <PlanningCallBookedControl programId={program.id} status={program} onSaved={onChange} align="start" />
    </div>
  );
}

/** Project page (Overview): status + toggle. Renders nothing before activation. */
export function PlanningCallStatusPanel({ programId, className = "" }) {
  const [status, setStatus] = useState(null);

  useEffect(() => {
    let alive = true;
    apiClient
      .get(`/programs/${programId}/planning-call`)
      .then((res) => alive && setStatus(res.data))
      .catch(() => alive && setStatus(null));
    return () => {
      alive = false;
    };
  }, [programId]);

  if (!status?.activated_at) return null;
  const state = planningCallState(status);
  return (
    <div className={className} data-testid="planning-call-status">
      <p className="label-text mb-1.5">Planning Call</p>
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant="secondary" className={`${state.tone} font-normal`}>
          {state.key === "call" ? <PhoneCall className="w-3 h-3 mr-1" /> : <CalendarClock className="w-3 h-3 mr-1" />}
          {state.label}
        </Badge>
        {status.can_edit && (
          <PlanningCallBookedControl
            programId={programId}
            status={status}
            onSaved={(fields) => setStatus((s) => ({ ...s, ...fields }))}
          />
        )}
      </div>
      <p className="text-[11px] text-muted-foreground mt-1">{state.detail}</p>
    </div>
  );
}

const NUMBER_FIELDS = [
  ["first_reminder_hours", "First reminder after (hours)"],
  ["reminder_interval_hours", "Then every (hours)"],
  ["escalate_after_days", "Call task after (days)"],
  ["max_reminders", "Stop after (reminders)"],
];

/** Header button + settings dialog. COO/Admin edit; PMs see it read-only. */
export function PlanningCallSettingsButton() {
  const [settings, setSettings] = useState(null);
  const [form, setForm] = useState(null);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await apiClient.get("/settings/planning-call");
      setSettings(res.data);
    } catch {
      setSettings(null);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (open && settings) {
      setForm({
        coordinator_user_id: settings.coordinator_user_id || "none",
        sender_name: settings.sender_name || "",
        sender_local_part: settings.sender_local_part || "planning",
        reply_to: settings.reply_to || "",
        first_reminder_hours: settings.first_reminder_hours,
        reminder_interval_hours: settings.reminder_interval_hours,
        escalate_after_days: settings.escalate_after_days,
        max_reminders: settings.max_reminders,
        send_mode: settings.send_mode,
        test_inbox: settings.test_inbox || "",
        ghl_calendar_id: settings.ghl_calendar_id || "",
      });
    }
  }, [open, settings]);

  if (!settings) return null;
  const mode = MODE_COPY[settings.effective_mode] || MODE_COPY.paused;
  const editable = settings.can_edit;
  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e?.target ? e.target.value : e }));
  const coordinatorPick = (settings.candidates || []).find((u) => u.id === form?.coordinator_user_id);
  const firstName = (coordinatorPick?.name || settings.coordinator?.name || "Arie").split(" ")[0];

  async function save() {
    setSaving(true);
    try {
      const payload = {
        ...form,
        coordinator_user_id: form.coordinator_user_id === "none" ? "" : form.coordinator_user_id,
      };
      NUMBER_FIELDS.forEach(([key]) => {
        payload[key] = Number(form[key]);
      });
      const res = await apiClient.put("/settings/planning-call", payload);
      setSettings(res.data);
      toast.success("Planning call automation saved");
      setOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't save the settings");
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <Button variant="outline" className="rounded-full" onClick={() => setOpen(true)} data-testid="planning-call-settings-btn">
        <Settings2 className="w-4 h-4 mr-2" />
        Planning call automation
        <Badge variant="secondary" className={`ml-2 ${mode.tone} font-normal`}>{mode.label}</Badge>
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Planning call automation</DialogTitle>
            <DialogDescription>
              The welcome email (with the Production Planning Call link) goes out at activation. From here it is
              sent by the coordinator, and the client is reminded until the call is booked.
            </DialogDescription>
          </DialogHeader>

          {form && (
            <div className="space-y-5">
              <div className="space-y-1.5">
                <Label className="text-xs">Mode</Label>
                <Select value={form.send_mode} onValueChange={set("send_mode")} disabled={!editable}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(MODE_COPY).map(([key, m]) => (
                      <SelectItem key={key} value={key}>{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">{(MODE_COPY[form.send_mode] || MODE_COPY.paused).help}</p>
                {settings.send_mode !== settings.effective_mode && (
                  <p className="text-xs text-amber-700">
                    Saved as {MODE_COPY[settings.send_mode]?.label}, but running as Paused until a coordinator
                    {settings.send_mode === "test" ? " and a test inbox are" : " is"} set.
                  </p>
                )}
                <p className="text-xs text-muted-foreground">
                  Switching on never reaches back: only projects whose welcome email goes out after that are chased.
                </p>
              </div>

              {form.send_mode === "test" && (
                <div className="space-y-1.5">
                  <Label className="text-xs">Test inbox</Label>
                  <Input value={form.test_inbox} onChange={set("test_inbox")} disabled={!editable} placeholder="you@therenderpros.com" />
                </div>
              )}

              <div className="space-y-1.5">
                <Label className="text-xs">Coordinator (sends the email, gets the call tasks)</Label>
                <Select value={form.coordinator_user_id} onValueChange={set("coordinator_user_id")} disabled={!editable}>
                  <SelectTrigger><SelectValue placeholder="Pick an HQ user" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Nobody (old sender, no reminders)</SelectItem>
                    {(settings.candidates || []).map((u) => (
                      <SelectItem key={u.id} value={u.id}>{u.name} — {u.email}</SelectItem>
                    ))}
                    {!editable && settings.coordinator && (
                      <SelectItem value={settings.coordinator.id}>{settings.coordinator.name}</SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label className="text-xs">Sender name</Label>
                  <Input value={form.sender_name} onChange={set("sender_name")} disabled={!editable} placeholder={`${firstName} at The Render Pros`} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Sender address</Label>
                  <div className="flex items-center gap-1">
                    <Input value={form.sender_local_part} onChange={set("sender_local_part")} disabled={!editable} className="min-w-0" />
                    <span className="text-xs text-muted-foreground whitespace-nowrap">@{settings.sender_domain}</span>
                  </div>
                </div>
                <div className="space-y-1.5 sm:col-span-2">
                  <Label className="text-xs">Replies go to</Label>
                  <Input
                    value={form.reply_to}
                    onChange={set("reply_to")}
                    disabled={!editable}
                    placeholder={coordinatorPick?.email || settings.coordinator?.email || "The coordinator's email"}
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs">GHL planning-call calendar ID</Label>
                <Input
                  value={form.ghl_calendar_id}
                  onChange={set("ghl_calendar_id")}
                  disabled={!editable}
                  placeholder="Zsq2xCCRkCR7mzk43AmY"
                />
                <p className="text-xs text-muted-foreground">
                  When the client books on this calendar (matched on their exact email address), the project is marked
                  booked and the reminders stop. Paste the ID or the booking link.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                {NUMBER_FIELDS.map(([key, label]) => (
                  <div key={key} className="space-y-1.5">
                    <Label className="text-xs">{label}</Label>
                    <Input type="number" min="1" value={form[key]} onChange={set(key)} disabled={!editable} />
                  </div>
                ))}
              </div>

              {settings.sender_preview && (
                <div className="rounded-md border bg-secondary/20 p-3 text-xs space-y-1">
                  <p><span className="text-muted-foreground">From:</span> {settings.sender_preview.from}</p>
                  <p><span className="text-muted-foreground">Reply-To:</span> {settings.sender_preview.reply_to}</p>
                  <p className="text-muted-foreground">As last saved.</p>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Close</Button>
            {editable && (
              <Button onClick={save} disabled={saving || !form}>{saving ? "Saving..." : "Save"}</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
